import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const VideoIntroduction = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const videoRef = useRef(null);

  const handlePlayClick = () => {
    setIsPlaying(true);
    setShowOverlay(false);
    if (videoRef?.current) {
      videoRef?.current?.play();
    }
  };

  const handlePauseClick = () => {
    setIsPlaying(false);
    if (videoRef?.current) {
      videoRef?.current?.pause();
    }
  };

  const overlayVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.6, 0.05, 0.01, 0.9]
      }
    },
    exit: {
      opacity: 0,
      scale: 1.1,
      transition: { duration: 0.4 }
    }
  };

  const playButtonVariants = {
    idle: { scale: 1 },
    hover: {
      scale: 1.1,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    },
    tap: { scale: 0.95 }
  };

  return (
    <section className="py-12 md:py-16 lg:py-24 px-4 md:px-6 lg:px-8 bg-gradient-to-b from-card to-background">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-8 md:mb-12 lg:mb-16">

          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 md:px-5 md:py-2.5 rounded-full bg-secondary/10 border border-secondary/20 mb-4 md:mb-6"
            whileHover={{ scale: 1.05 }}>

            <Icon name="Video" size={16} color="var(--color-secondary)" />
            <span className="text-xs md:text-sm lg:text-base font-medium text-secondary">
              Get to Know Me
            </span>
          </motion.div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-5 lg:mb-6">
            <span className="bg-gradient-to-r from-secondary via-accent to-primary bg-clip-text text-transparent">
              My Story in Motion
            </span>
          </h2>
          <p className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto">
            Watch my journey from IT engineer to entrepreneur, mentor, and creative director
          </p>
        </motion.div>

        {/* Video Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl">

          {/* Video Thumbnail/Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/20 to-accent/20">
            <Image
              src="https://images.unsplash.com/photo-1686061593147-0486fbe9448b"
              alt="Professional video production setup showing modern recording studio with professional lighting equipment, high-end camera on tripod, and sleek minimalist background for content creation"
              className="w-full h-full object-cover opacity-40" />

          </div>

          {/* Animated Overlay */}
          <AnimatePresence>
            {showOverlay &&
            <motion.div
              variants={overlayVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="absolute inset-0 bg-gradient-to-br from-background/80 via-card/70 to-background/80 backdrop-blur-sm flex flex-col items-center justify-center">

                {/* Play Button */}
                <motion.button
                variants={playButtonVariants}
                initial="idle"
                whileHover="hover"
                whileTap="tap"
                onClick={handlePlayClick}
                className="relative w-20 h-20 md:w-24 md:h-24 lg:w-32 lg:h-32 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-2xl mb-6 md:mb-8"
                aria-label="Play video introduction">

                  <motion.div
                  className="absolute inset-0 rounded-full bg-primary/30"
                  animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.5, 0, 0.5]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }} />

                  <Icon name="Play" size={32} color="white" className="ml-1" />
                </motion.button>

                {/* Text Content */}
                <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-center px-4">

                  <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground mb-2 md:mb-3">
                    Watch My Introduction
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    3 minutes • Behind the scenes of innovation
                  </p>
                </motion.div>

                {/* Decorative Elements */}
                <motion.div
                className="absolute top-10 left-10 w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/10 blur-xl"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }} />

                <motion.div
                className="absolute bottom-10 right-10 w-20 h-20 md:w-24 md:h-24 rounded-full bg-secondary/10 blur-xl"
                animate={{
                  scale: [1.2, 1, 1.2],
                  opacity: [0.6, 0.3, 0.6]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }} />

              </motion.div>
            }
          </AnimatePresence>

          {/* Video Element (Mock) */}
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            poster="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg"
            onEnded={() => {
              setIsPlaying(false);
              setShowOverlay(true);
            }}>

            <source src="#" type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          {/* Video Controls Overlay */}
          {isPlaying &&
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background/80 to-transparent p-4 md:p-6">

              <div className="flex items-center justify-between">
                <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={handlePauseClick}
                className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/20 backdrop-blur-sm flex items-center justify-center"
                aria-label="Pause video">

                  <Icon name="Pause" size={20} color="white" />
                </motion.button>

                <div className="flex-1 mx-4">
                  <div className="h-1 bg-muted rounded-full overflow-hidden">
                    <motion.div
                    className="h-full bg-primary"
                    initial={{ width: '0%' }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 180, ease: "linear" }} />

                  </div>
                </div>

                <div className="text-xs md:text-sm text-white font-medium">
                  0:00 / 3:00
                </div>
              </div>
            </motion.div>
          }
        </motion.div>

        {/* Video Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="grid grid-cols-3 gap-4 md:gap-6 lg:gap-8 mt-8 md:mt-10 lg:mt-12 max-w-3xl mx-auto">

          {[
          { icon: 'Eye', value: '15K+', label: 'Views' },
          { icon: 'ThumbsUp', value: '2.5K', label: 'Likes' },
          { icon: 'Share2', value: '850', label: 'Shares' }]?.
          map((stat, index) =>
          <motion.div
            key={index}
            className="text-center p-4 md:p-5 lg:p-6 rounded-xl bg-card/50 backdrop-blur-sm border border-border"
            whileHover={{ y: -5, boxShadow: '0 10px 30px rgba(139, 92, 246, 0.2)' }}>

              <div className="flex justify-center mb-2 md:mb-3">
                <Icon name={stat?.icon} size={24} color="var(--color-secondary)" />
              </div>
              <div className="text-xl md:text-2xl lg:text-3xl font-bold text-secondary mb-1">
                {stat?.value}
              </div>
              <div className="text-xs md:text-sm text-muted-foreground">
                {stat?.label}
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>);





};

export default VideoIntroduction;